import { useState } from 'react';
import { getPropertySubTypeKey, getPropertyTypeKey } from '@pkgs/components/utils/translationKeyUtils';
import { reorderForLeftToRightDisplay } from '@pkgs/components/utils/gridOrder';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import ArrowDownIcon from '@pkgs/components/assets/icons/xsmall/Arrow-Thin-Down.svg';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { getEntries, type SearchFilterRequestPropertyTypeSubType } from '@ev-tech/search-modules-api';

import { Checkbox } from '../Checkbox/Checkbox';

import styles from './ObjectTypeFilter.module.css';

export const EXPANDABLE_CHECKBOX_TEST_IDS = {
  propertyTypeCheckbox: 'search-components__expandable-checkbox__property-type-checkbox',
  propertyTypeCheckboxWrapper: 'search-components__expandable-checkbox__property-type-checkbox-wrapper',
  propertySubTypeCheckbox: 'search-components__expandable-checkbox__property-sub-type-checkbox',
} as const;

type ExpandableCheckboxProps = {
  propertyType: keyof SearchFilterRequestPropertyTypeSubType;
};

export const ExpandableCheckbox = ({ propertyType }: ExpandableCheckboxProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useAppContext();
  const propertyTypeState = useSearchStore((state) => state.propertyTypeState);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const setPropertyType = useSearchStore((state) => state.setPropertyType);
  const setPropertySubType = useSearchStore((state) => state.setPropertySubType);
  const { breakpoint = 0 } = useBreakpoint();

  const subTypes = propertyTypeState[objectTypeCategory][propertyType]?.subTypes;

  const hasSelectedSubTypes = !!subTypes && Object.values(subTypes).some((subType) => subType.selected);

  const isAllSubTypesSelected = !!subTypes && Object.values(subTypes).every((subType) => subType.selected);

  const isIndeterminate = !!subTypes && hasSelectedSubTypes && !isAllSubTypesSelected;

  const isCheckboxActive =
    (!!propertyTypeState[objectTypeCategory][propertyType] && propertyTypeState[objectTypeCategory][propertyType].selected) ||
    hasSelectedSubTypes;
  const propertyTypeLabelTranslationKey = getPropertyTypeKey(propertyType);
  const propertyTypeLabel = propertyTypeLabelTranslationKey ? t(propertyTypeLabelTranslationKey) : propertyType;

  const handlePropertyTypeChange = () => {
    if (subTypes && !isAllSubTypesSelected) {
      setIsExpanded(true);
    }
    setPropertyType(propertyType);
  };

  const columnCount = breakpoint >= Breakpoints.TABLET_PORTRAIT ? 2 : 1;

  return (
    <div className={styles.expandableCheckboxContainer}>
      <div className={styles.propertyTypeCheckboxWrapper}>
        <Checkbox
          className={styles.propertyTypeCheckbox}
          value={propertyType}
          aria-label={propertyTypeLabel}
          label={subTypes ? undefined : propertyTypeLabel}
          isSelected={isCheckboxActive}
          isIndeterminate={isIndeterminate}
          onChange={handlePropertyTypeChange}
          data-testid={EXPANDABLE_CHECKBOX_TEST_IDS.propertyTypeCheckbox}
        />

        {subTypes && (
          <button
            type="button"
            className={styles.expandableGroupLabel}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            data-testid={EXPANDABLE_CHECKBOX_TEST_IDS.propertyTypeCheckboxWrapper}
          >
            <Copy text={propertyTypeLabel} />
            <ArrowDownIcon className={styles.arrowDownIcon} />
          </button>
        )}
      </div>

      {isExpanded && subTypes && (
        <div className={styles.propertySubTypeCheckboxGroup}>
          {reorderForLeftToRightDisplay(getEntries(subTypes), columnCount).map(([subType, data]) => {
            const subTypeTranslationKey = getPropertySubTypeKey(subType);
            const subTypeLabel = subTypeTranslationKey ? t(subTypeTranslationKey) : subType;
            return (
              <div key={subType} className={styles.subTypeCheckboxItem}>
                <Checkbox
                  value={subType}
                  label={subTypeLabel}
                  aria-label={subTypeLabel}
                  isSelected={data?.selected}
                  onChange={() => setPropertySubType(propertyType, subType)}
                  data-testid={EXPANDABLE_CHECKBOX_TEST_IDS.propertySubTypeCheckbox}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
